// Domain: transaction split computation

import { SplitDetails } from "@/types/GroupTransaction";
import { roundTo } from "utils/numberUtils";

// -----------------------------------------------------------------------------
//   Helpers
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
export function toAmount(value: string | number): number {
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(n) ? roundTo(n, 2) : 0;
}

// -----------------------------------------------------------------------------
/**
 * Computes equal splits for a given amount and list of people.
 * Returns splits as strings (for form state compatibility).
 */
export function computeEqualSplits(amount: string | number, people: string[] = []): Record<string, string> {
    const total = toAmount(amount);
    const len = people.length || 1;
    const share = total / len;
    return people.reduce<Record<string, string>>((acc, person) => {
        acc[person] = share.toFixed(2);
        return acc;
    }, {});
}

/**
 * Computes equal splits for a given amount and people map (for numeric splits).
 */
export function computeEqualSplitsNumeric(
    amount: string | number,
    people: SplitDetails = {},
    decimals: number = 2
): SplitDetails {
    const totalAmount: number = toAmount(amount);
    const personIds = Object.keys(people);
    const peopleCount: number = personIds.length || 1;
    const factor = 10 ** decimals;
    const totalUnits = Math.round(totalAmount * factor);
    const baseUnits = Math.floor(totalUnits / peopleCount);
    const remainderUnits = totalUnits - (baseUnits * peopleCount);

    return personIds.reduce<Record<string, number>>((accumulator, person, index) => {
        const units = baseUnits + (index < remainderUnits ? 1 : 0);
        accumulator[person] = roundTo(units / factor, decimals);
        return accumulator;
    }, {});
}

// -----------------------------------------------------------------------------
export function isCustomValid(
    amount: string | number, 
    customSplits: Record<string, string | number>, 
    epsilon: number = 0.01
): boolean {
    const expected = toAmount(amount);
    const sum = Object.values(customSplits || {}).reduce((acc: number, v) => {
        return acc + toAmount(v as string | number);
    }, 0);
    return Math.abs(sum - expected) <= epsilon;
}

export function isCustomValidNumeric(
    amount: string | number,
    customSplits: SplitDetails,
    epsilon: number = 0.01
): boolean {
    const expectedTotal: number = toAmount(amount);
    const sumOfSplits: number = Object.values(customSplits || {}).reduce(
        (runningTotal: number, value: string | number) => runningTotal + toAmount(value),
        0
    );
    return Math.abs(sumOfSplits - expectedTotal) <= epsilon;
}

// -----------------------------------------------------------------------------
export function toggleInArray(arr: string[] = [], value: string, checked: boolean) {
    if (checked) {
        return arr.includes(value) ? arr : [...arr, value];
    } else {
        return arr.filter(v => v !== value);
    }
}

// -----------------------------------------------------------------------------
export function mapSplitDetailsToFormState(splitDetails: Record<string, number> = {}): [string[], Record<string, string | number>] {
    const splitBetween = Object.keys(splitDetails);
    const customSplits = Object.entries(splitDetails).reduce<Record<string, string | number>>((acc, [username, amount]) => {
        acc[username] = amount;
        return acc;
    }, {});
    
    return [splitBetween, customSplits];
}

// -----------------------------------------------------------------------------
/**
 * Backward-compatible split type inference when persisted transactions do not
 * include splitType (legacy server payloads).
 */
export function inferSplitTypeFromDetails(
    splitDetails: Record<string, number> = {},
    epsilon: number = 0.01
): "equal" | "custom" {
    const participantAmounts = Object.values(splitDetails)
        .map((value) => toAmount(value))
        .filter((value) => value > 0);

    // 0-1 participants does not carry enough information to distinguish modes.
    if (participantAmounts.length <= 1) return "equal";

    const min = Math.min(...participantAmounts);
    const max = Math.max(...participantAmounts);
    return Math.abs(max - min) <= epsilon ? "equal" : "custom";
}

// -----------------------------------------------------------------------------
export function groupTransactionToEditState(transaction: {
    _id?: string;
    name?: string;
    amount?: number;
    payer?: string;
    splitType?: "equal" | "custom";
    splitDetails?: Record<string, number>;
    currency?: string;
    category?: string;
    date?: string;
    type?: "expense" | "income" | "settlement";
    liquidityPoolId?: string;
}): {
    _id?: string;
    name: string;
    amount: string;
    payer: string;
    splitType: "equal" | "custom";
    splitBetween: string[];
    customSplits: Record<string, string | number>;
    splitDetails?: Record<string, number>;
    currency: string;
    category?: string;
    date?: string;
    type?: "expense" | "income" | "settlement";
    liquidityPoolId?: string;
} {
    const [splitBetween, customSplits] = mapSplitDetailsToFormState(transaction.splitDetails || {});
    const splitType = transaction.splitType ?? inferSplitTypeFromDetails(transaction.splitDetails || {});
    
    return {
        _id: transaction._id,
        name: transaction.name || "",
        amount: (transaction.amount || 0).toString(),
        payer: transaction.payer || "",
        splitType,
        splitBetween,
        customSplits,
        splitDetails: transaction.splitDetails,
        currency: transaction.currency || "",
        category: transaction.category,
        date: transaction.date,
        type: transaction.type,
        liquidityPoolId: transaction.liquidityPoolId,
    };
}
